import * as stylex from "@stylexjs/stylex";
import { copy } from "@/copy";
import type { CalendarEvent, WeatherLocation } from "@/domain/schemas";
import { type Clock, clockAt } from "@/domain/time";
import { timeZoneName, type ZoneChange, zoneChange } from "@/domain/zoneName";
import "@/theme/tokens.stylex";
import { colors, radii, space } from "@/theme/tokens.stylex";
import "@/theme/type.stylex";
import { type } from "@/theme/type.stylex";
import { Button } from "@/ui/Button";
import { ColorBlock } from "@/ui/ColorBlock";
import { Eyebrow } from "@/ui/Eyebrow";
import { placeLabel } from "../location";
export type ZoneExample = Pick<CalendarEvent, "title" | "startAt">;
type ZoneConfirmProps = {
  place: WeatherLocation;
  from: string;
  now: Date;
  clock: Clock;
  example?: ZoneExample | undefined;
  onConfirm: () => void;
  onKeep: () => void;
};
const styles = {
  block: {
    k1xSpc: "x78zum5",
    kXwgrk: "xdt5ytf",
    kOIVth: "x1665zp3",
    $$css: true
  }
};
export function ZoneConfirm({
  place,
  from,
  now,
  clock,
  example,
  onConfirm,
  onKeep
}: ZoneConfirmProps) {
  const text = copy.location.confirm;
  const to = place.timeZone ?? from;
  const [fromName, toName] = [timeZoneName(from), timeZoneName(to)];
  const change = zoneChange(from, to, now);
  const at = (iso: string, zone: string) => clockAt(iso, clock, zone);
  const showsExample = example && change.kind === "shift";
  return <ColorBlock tone={4} padding="clamp(20px, 2.4vw, 32px)" role="group" aria-label={text.title(toName)} xstyle={styles.block}>
      <div className="x78zum5 xdt5ytf x152posx">
        <Eyebrow>{text.eyebrow}</Eyebrow>
        <span className="xneq65o x1v94iar xrg9stw x1uo3zyz">{text.title(toName)}</span>
        <span className="x19d36u7 x15i68ds xfrs9s4 x1fzhlzt">{changeBody(change, placeLabel(place), toName)}</span>
      </div>
      <div className="xrvj5dj xnby9oq x1hu4l6k">
        <Tile label={text.before(fromName)} time={at(now.toISOString(), from)} />
        <Tile label={text.after(toName)} time={at(now.toISOString(), to)} />
      </div>
      {showsExample && <span className="x1j61zf2 x15i68ds xfrs9s4 x1fzhlzt">
          {text.example(example.title, at(example.startAt, from), at(example.startAt, to))}
        </span>}
      <div className="x78zum5 x1a02dak x6s0dn4 x1hu4l6k">
        <Button onClick={onConfirm}>{text.switch(toName)}</Button>
        <Button variant="secondary" onClick={onKeep}>
          {text.keep(fromName)}
        </Button>
      </div>
    </ColorBlock>;
}
function changeBody(change: ZoneChange, place: string, zone: string): string {
  const text = copy.location.confirm;
  if (change.kind === "same-now") return text.sameNow(place, zone);
  return text.moves(place, zone, text.hours(change.hours), change.direction);
}
function Tile({
  label,
  time
}: {
  label: string;
  time: string;
}) {
  return <div className="x78zum5 xdt5ytf x167g77z xeuugli xh540kj x1a11ngh x1fv5t7x x1vvitgl">
      <span className="xr0iwhn xhunfcg xkv8mnm xtvhhri">{label}</span>
      <span className="x14lodcv x1iikomf x75m3dq xss6m8b x1159mfc">{time}</span>
    </div>;
}